import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Setting {
  id: string;
  key: string;
  value_ar?: string;
  value_en?: string;
  data_type: string;
  category: string;
  is_public: boolean;
  description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const useSettings = () => {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async (): Promise<Setting[]> => {
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('*')
          .eq('is_public', true)
          .order('sort_order', { ascending: true });

        if (error) {
          console.error('Settings error:', error);
          return [];
        }

        return data || [];
      } catch (error) {
        console.error('Settings query error:', error);
        return [];
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

export const useSettingValue = (key: string, language: 'ar' | 'en' = 'ar') => {
  const { data: settings } = useSettings();
  
  const setting = settings?.find(s => s.key === key);
  if (!setting) return null;
  
  return language === 'ar' ? setting.value_ar : setting.value_en;
};

export const useSiteSettings = () => {
  const { data: settings } = useSettings();
  
  const getSetting = (key: string, language: 'ar' | 'en' = 'ar') => {
    const setting = settings?.find(s => s.key === key);
    if (!setting) return null;
    return language === 'ar' ? setting.value_ar : setting.value_en;
  };

  return {
    settings,
    getSetting,
    companyName: (language: 'ar' | 'en' = 'ar') => getSetting('company_name', language) || (language === 'ar' ? 'أباسك' : 'ABASC HOLDING'),
    companyDescription: (language: 'ar' | 'en' = 'ar') => getSetting('company_description', language),
    contactEmail: getSetting('contact_email') || 'info@abasc.g-ksa.com',
    contactPhone: getSetting('contact_phone') || '+966115157381',
    contactAddress: (language: 'ar' | 'en' = 'ar') => getSetting('contact_address', language) || (language === 'ar' ? 'طريق ابي بكر الصديق الفرعي، المرسلات، الرياض' : 'Riyadh, Saudi Arabia'),
    socialLinks: {
      facebook: getSetting('social_facebook'),
      twitter: getSetting('social_twitter'),
      linkedin: getSetting('social_linkedin'),
      instagram: getSetting('social_instagram'),
      youtube: getSetting('social_youtube'),
    }
  };
};